import { Property, ListingLifecycleStatus, ListingVerificationStatus } from '../types';

/**
 * UTC-safe check to determine whether a listing is expired.
 * Compares millisecond timestamps using UTC time to eliminate client-clock deviations.
 * If expiry date is in the future, it is NEVER expired.
 */
export function isListingExpired(expiresAt?: string | number | Date | any | null): boolean {
  if (!expiresAt) return false;

  try {
    let expiryMillis = 0;
    if (typeof expiresAt === 'number') {
      expiryMillis = expiresAt;
    } else if (typeof expiresAt === 'string') {
      expiryMillis = new Date(expiresAt).getTime();
    } else if (expiresAt instanceof Date) {
      expiryMillis = expiresAt.getTime();
    } else if (typeof expiresAt.toMillis === 'function') {
      expiryMillis = expiresAt.toMillis();
    } else if (typeof expiresAt.toDate === 'function') {
      expiryMillis = expiresAt.toDate().getTime();
    } else if (expiresAt._seconds) {
      expiryMillis = expiresAt._seconds * 1000;
    }

    if (!expiryMillis || isNaN(expiryMillis)) return false;

    return Date.now() >= expiryMillis;
  } catch {
    return false;
  }
}

/**
 * Single authoritative check for public indexability and visibility.
 * Used identically across map markers, search results, category pages,
 * property detail pages, and static SEO generators.
 */
export function isListingPublic(property?: Partial<Property> | any | null): boolean {
  if (!property) return false;

  // Must not be test artifacts
  const title = (property.title || '').toLowerCase();
  const desc = (property.description || '').toLowerCase();
  const id = (property.id || '').toLowerCase();
  if (
    title.includes('fix: post-ad crash') ||
    title.includes('commit-message') ||
    desc.includes('wasdfok88') ||
    desc.includes('.agents/rules') ||
    id.startsWith('prop-test-') ||
    id.startsWith('prop-mock-') ||
    id.startsWith('prop-sample-')
  ) {
    return false;
  }

  const rawStatus = (property.status || '').toLowerCase().trim();

  // Normalize legacy status values to strict schema
  const status: ListingLifecycleStatus =
    rawStatus === 'active' || rawStatus === 'published'
      ? 'published'
      : rawStatus === 'pending' || rawStatus === 'pending_review'
      ? 'pending_review'
      : rawStatus === 'draft'
      ? 'draft'
      : rawStatus === 'expired'
      ? 'expired'
      : rawStatus === 'rejected'
      ? 'rejected'
      : rawStatus === 'archived' || rawStatus === 'deleted' || rawStatus === 'sold' || rawStatus === 'rented'
      ? 'archived'
      : 'draft';

  // Only published listings can be public
  if (status !== 'published') {
    return false;
  }

  // isPublic flag (defaults to true if published in legacy rows, but false if explicitly false)
  if (property.isPublic === false) {
    return false;
  }

  // Expiry check
  const expiresAt = property.expiresAt || property.expires_at;
  if (isListingExpired(expiresAt)) {
    return false;
  }

  return true;
}

export interface VerificationDisplayInfo {
  status: ListingVerificationStatus;
  isVerified: boolean;
  isPending: boolean;
  isRejected: boolean;
  isUnverified: boolean;
  badgeText: string;
  shortBadgeText: string;
  badgeClass: string;
  badgeClasses: string;
}

/**
 * Authoritative Cadastre / Title Deed Verification helper.
 * Independent of listing visibility.
 * A listing displays "Titre Foncier / Cadastre Verified" ONLY when verificationStatus === "verified".
 */
export function getListingVerificationInfo(
  property?: Partial<Property> | any | null,
  isFr: boolean = false
): VerificationDisplayInfo {
  const rawStatus = (property?.verificationStatus || property?.verification_status || '').toLowerCase().trim();

  const status: ListingVerificationStatus =
    rawStatus === 'verified' || property?.verified === true
      ? 'verified'
      : rawStatus === 'pending' || rawStatus === 'documents_uploaded'
      ? 'pending'
      : rawStatus === 'rejected'
      ? 'rejected'
      : 'unverified';

  switch (status) {
    case 'verified':
      return {
        status: 'verified',
        isVerified: true,
        isPending: false,
        isRejected: false,
        isUnverified: false,
        badgeText: isFr ? 'Titre Foncier / Cadastre Vérifié' : 'Titre Foncier / Cadastre Verified',
        shortBadgeText: isFr ? 'Cadastre Vérifié' : 'Cadastre Verified',
        badgeClass: 'bg-[#094426] text-white border border-emerald-600',
        badgeClasses: 'bg-[#094426] text-white border border-emerald-600',
      };
    case 'pending':
      return {
        status: 'pending',
        isVerified: false,
        isPending: true,
        isRejected: false,
        isUnverified: false,
        badgeText: isFr ? 'Audit Cadastral en Cours' : 'Cadastre Audit Pending',
        shortBadgeText: isFr ? 'Audit en Cours' : 'Audit Pending',
        badgeClass: 'bg-amber-100 text-amber-950 border border-amber-300',
        badgeClasses: 'bg-amber-100 text-amber-950 border border-amber-300',
      };
    case 'rejected':
      return {
        status: 'rejected',
        isVerified: false,
        isPending: false,
        isRejected: true,
        isUnverified: false,
        badgeText: isFr ? 'Dossier Cadastral Non Conforme' : 'Cadastre Audit Rejected',
        shortBadgeText: isFr ? 'Rejeté' : 'Rejected',
        badgeClass: 'bg-red-100 text-red-950 border border-red-300',
        badgeClasses: 'bg-red-100 text-red-950 border border-red-300',
      };
    case 'unverified':
    default:
      return {
        status: 'unverified',
        isVerified: false,
        isPending: false,
        isRejected: false,
        isUnverified: true,
        badgeText: isFr ? 'Non Vérifié' : 'Not Yet Verified',
        shortBadgeText: isFr ? 'Non Vérifié' : 'Not Verified',
        badgeClass: 'bg-slate-100 text-slate-700 border border-slate-300',
        badgeClasses: 'bg-slate-100 text-slate-700 border border-slate-300',
      };
  }
}

/**
 * Calculates "Listed X days ago" ONLY from publishedAt.
 * Never shows relative published date for drafts, pending review, or expired listings.
 * An expired listing must not display as newly listed.
 */
export function getListingPublishedRelativeDate(
  property?: Partial<Property> | any | null,
  isFr: boolean = false
): string | null {
  if (!property) return null;

  // Must be active and published
  if (!isListingPublic(property)) {
    return null;
  }

  const dateVal = property.publishedAt || property.published_at;
  if (!dateVal) return null;

  try {
    let pubMillis = 0;
    if (typeof dateVal === 'number') {
      pubMillis = dateVal;
    } else if (typeof dateVal === 'string') {
      pubMillis = new Date(dateVal).getTime();
    } else if (dateVal instanceof Date) {
      pubMillis = dateVal.getTime();
    } else if (typeof dateVal.toMillis === 'function') {
      pubMillis = dateVal.toMillis();
    } else if (typeof dateVal.toDate === 'function') {
      pubMillis = dateVal.toDate().getTime();
    } else if (dateVal._seconds) {
      pubMillis = dateVal._seconds * 1000;
    }

    if (!pubMillis || isNaN(pubMillis)) return null;

    const nowMillis = Date.now();
    const diffMs = nowMillis - pubMillis;

    // If timestamp is slightly in future due to clock skew, show today
    if (diffMs < 0) {
      return isFr ? "Aujourd'hui" : 'Today';
    }

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return isFr ? "Aujourd'hui" : 'Today';
    } else if (diffDays === 1) {
      return isFr ? 'Hier' : 'Yesterday';
    } else if (diffDays > 1 && diffDays < 7) {
      return isFr ? `Il y a ${diffDays}j` : `${diffDays}d ago`;
    }

    const pubDate = new Date(pubMillis);
    return pubDate.toLocaleDateString(isFr ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return null;
  }
}

/**
 * User-friendly lifecycle label for owner and admin dashboards.
 */
export function getListingLifecycleLabel(
  status?: ListingLifecycleStatus | string | null,
  isFr: boolean = false
): { label: string; badgeClass: string } {
  const s = (status || 'draft').toLowerCase().trim();

  switch (s) {
    case 'published':
    case 'active':
      return {
        label: isFr ? 'Publiée' : 'Published',
        badgeClass: 'bg-emerald-100 text-[#094426] border border-emerald-300',
      };
    case 'pending_review':
    case 'pending':
      return {
        label: isFr ? 'En attente de validation' : 'Pending Review',
        badgeClass: 'bg-amber-100 text-amber-900 border border-amber-300',
      };
    case 'expired':
      return {
        label: isFr ? 'Expirée' : 'Expired',
        badgeClass: 'bg-red-100 text-red-900 border border-red-300',
      };
    case 'rejected':
      return {
        label: isFr ? 'Rejetée' : 'Rejected',
        badgeClass: 'bg-red-100 text-red-900 border border-red-300',
      };
    case 'archived':
    case 'sold':
    case 'rented':
      return {
        label: isFr ? 'Archivée' : 'Archived',
        badgeClass: 'bg-slate-100 text-slate-700 border border-slate-300',
      };
    case 'draft':
    default:
      return {
        label: isFr ? 'Brouillon' : 'Draft',
        badgeClass: 'bg-slate-100 text-slate-700 border border-slate-300',
      };
  }
}

/**
 * Robust surface area extractor that normalizes land & building surface measurements to square meters (m²).
 * Handles string formats like "500 m²", "1,200 sqm", "0.5 ha" (5,000 m²), and raw numeric attributes.
 */
export function getPropertyAreaSqm(property?: Partial<Property> | any | null): number | null {
  if (!property) return null;

  // 1. Check direct calculatedAreaSqm (number)
  if (typeof property.calculatedAreaSqm === 'number' && !isNaN(property.calculatedAreaSqm) && property.calculatedAreaSqm > 0) {
    return Math.round(property.calculatedAreaSqm);
  }

  // 2. Check categoryAttributes surface fields
  const catArea = property.categoryAttributes?.surface_area || property.categoryAttributes?.surface;
  if (typeof catArea === 'number' && !isNaN(catArea) && catArea > 0) {
    return Math.round(catArea);
  }

  // 3. Check specs.area (string or number)
  const rawArea = property.specs?.area || (typeof catArea === 'string' ? catArea : null);
  if (!rawArea) return null;

  if (typeof rawArea === 'number' && !isNaN(rawArea)) {
    return Math.round(rawArea);
  }

  if (typeof rawArea === 'string') {
    const cleanStr = rawArea.toLowerCase().trim();

    // Check for hectare units (1 ha = 10,000 m²)
    const haMatch = cleanStr.match(/([0-9]+(?:[.,][0-9]+)?)\s*(?:ha|hectare|hectares)/);
    if (haMatch) {
      const num = parseFloat(haMatch[1].replace(',', '.'));
      if (!isNaN(num) && num > 0) {
        return Math.round(num * 10000);
      }
    }

    // Check for m² / sqm / sq m / square meters / m2
    const numMatch = cleanStr.replace(/[^0-9.,]/g, ' ').trim().split(/\s+/)[0];
    if (numMatch) {
      const num = parseFloat(numMatch.replace(/,/g, ''));
      if (!isNaN(num) && num > 0) {
        return Math.round(num);
      }
    }
  }

  return null;
}

/**
 * Authoritative check for Titre Foncier (Land Title Deed) status.
 * Returns true if the listing has verified land title credentials or certified title deed status.
 */
export function hasPropertyTitleDeed(property?: Partial<Property> | any | null): boolean {
  if (!property) return false;

  if (property.hasTitleDeed === true) return true;
  if (property.titleNumber && typeof property.titleNumber === 'string' && property.titleNumber.trim().length >= 2) return true;
  if (property.verified === true) return true;

  const rawVer = (property.verificationStatus || property.verification_status || '').toLowerCase().trim();
  if (rawVer === 'verified' || rawVer === 'documents_uploaded') return true;

  const badge = (property.categoryBadge || '').toUpperCase();
  if (badge.includes('TITRÉ') || badge.includes('TITRE') || badge.includes('TITLED')) return true;

  const fullText = `${property.title || ''} ${property.description || ''}`.toLowerCase();
  if (
    fullText.includes('titre foncier') ||
    fullText.includes('titré') ||
    fullText.includes('land title') ||
    fullText.includes('certificat de propriété')
  ) {
    return true;
  }

  return false;
}
